import { IElement } from "core-integration/src/store/elements/types";
import { IElementPriceChange } from "core-integration/src/store/stats/types";
import ElementDialogButton from "plos/src/components/ElementDialogButton";
import ElementInTable from "plos/src/components/ElementInTable";
import { Paginator, paginate } from "plos/src/components/Paginator";
import { Cell, Column, Table } from "plos/src/components/Table";
import { rowStyles } from "plos/src/components/Table/tables.css";
import { getDifficultyColor } from "plos/src/styles/difficultyColors";
import { fixedTable, scrollContainer } from "plos/src/styles/utils.css";
import { integerToMoneyWithCurrency } from "plos/src/utils/money";
import { Row, TableBody, TableHeader } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { formatRawAsDate } from "shared-utils/src/datetime";
import { getPriceChangeDelta } from "../PriceChangeFilters/utils";
import styles from "../priceChanges.module.css";
import { SortableColumn } from "../SortableColumn";
import { PriceChangesTableProps } from "../types";

const dateFormat = "d MMM yyyy";

interface RecentChangesTableProps extends PriceChangesTableProps {
  recentPriceChanges: IElementPriceChange[];
  elementsById: Record<number, IElement>;
}

const RecentChangesTable = ({
  recentPriceChanges,
  elementsById,
  showElementDialog,
  teamsById,
  currencyDivisor,
  handleSortChange,
  sortDescriptor,
  page,
  setPage,
}: RecentChangesTableProps) => {
  const { t } = useTranslation("priceChanges");

  const { data, totalPages } = paginate(recentPriceChanges, page, 20);

  return (
    <>
      <div className={scrollContainer}>
        <div className={styles.statsTableWrapper}>
          <Table
            className={fixedTable}
            aria-label={t("history.title", "Price Change History")}
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
          >
            <TableHeader>
              <Column className={styles.statsInfoCol} />
              <SortableColumn
                id="player"
                isRowHeader
                label={t("player", "Player")}
                className={styles.statsElementCol}
              />
              <SortableColumn
                id="date"
                label={t("history.date", "Date")}
                className={styles.statColWide}
              />
              <SortableColumn
                id="change"
                label={t("history.change", "Change")}
                className={styles.priceCol}
              />
              <SortableColumn
                id="previousPrice"
                label={t("history.previousPrice", "Previous Price")}
                className={styles.priceCol}
              />
              <SortableColumn
                id="newPrice"
                label={t("history.newPrice", "New Price")}
                className={styles.priceCol}
              />
            </TableHeader>
            <TableBody
              renderEmptyState={() => (
                <div className={styles.emptyState}>
                  {t("history.noChanges", "No price changes to display.")}
                </div>
              )}
            >
              {data.map((e) => {
                const element = elementsById[e.element];
                const change = getPriceChangeDelta(e);

                return (
                  <Row
                    key={`${e.element}-${e.changed_at}`}
                    className={rowStyles}
                  >
                    {/* Info */}
                    <Cell className={styles.statsInfoCell}>
                      <ElementDialogButton
                        elementId={e.element}
                        variant="list"
                      />
                    </Cell>
                    {/* Player */}
                    <Cell className={styles.statsElementCell}>
                      <ElementInTable
                        element={element}
                        team={teamsById[e.team]}
                        actionMe={() => showElementDialog(e.element)}
                        renderDreamTeam={element.in_dreamteam}
                      />
                    </Cell>
                    {/* Date */}
                    <Cell>{formatRawAsDate(e.changed_at, dateFormat)}</Cell>
                    {/* Change */}
                    <Cell>
                      <span
                        className={`${styles.chip} ${getDifficultyColor(change > 0 ? 1 : 5)}`}
                      >
                        {change > 0 ? "+" : "-"}
                        {integerToMoneyWithCurrency(
                          Math.abs(change),
                          currencyDivisor,
                        )}
                      </span>
                    </Cell>
                    {/* Previous Price */}
                    <Cell>
                      {integerToMoneyWithCurrency(
                        e.cost_before,
                        currencyDivisor,
                      )}
                    </Cell>
                    {/* New Price */}
                    <Cell>
                      {integerToMoneyWithCurrency(
                        e.cost_after,
                        currencyDivisor,
                      )}
                    </Cell>
                  </Row>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
      {totalPages > 0 && (
        <Paginator
          totalPages={totalPages}
          page={page}
          setPage={setPage}
          variant="text"
        />
      )}
    </>
  );
};

export default RecentChangesTable;
